// Copyright ©️ 2026 eVolve MEP, LLC

import React, { type ReactNode, useCallback, useMemo, useRef, useState } from 'react';

import { ApsConfiguration, SdkManagerBuilder } from '@aps_sdk/autodesk-sdkmanager';
import { DataManagementClient, type HubData, type ProjectData } from '@aps_sdk/data-management';
import {
  type AutodeskToken,
  type AutodeskCloudModel,
  extractErrorMessage,
  useEvolveGet,
  useEvolvePost,
} from '@evolvemep/api-ts';
import { notifications } from '@mantine/notifications';
import { addMinutes, isAfter } from 'date-fns';

import { isKeyOf } from 'helpers/isKeyOf';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { strongEntries } from 'helpers/strongEntries';
import { useGeneralContext } from 'helpers/useGeneralContext';
import { useLocalStorage, getLocalStorageWrapped } from 'hooks/useLocalStorage';

import { type AutodeskRedirectState, autodeskRedirectUri, buildAutodeskStateVar } from './helpers';

const isExpired = (autodeskAuth: AutodeskToken | null | undefined) =>
  isNil(autodeskAuth) ||
  isAfter(
    // Refresh the token once there's only 3 minutes left,
    // to avoid using a token right as it expires
    addMinutes(new Date(), 3),
    new Date(autodeskAuth.expiresAt * 1000),
  );

type AutodeskAuthContextType = {
  autodeskAuth: AutodeskToken | undefined;
  setAutodeskAuth: (token: AutodeskToken) => void;
  /**
   * Gets an Autodesk cloud token via callback.
   * Used by the Forge Viewer.
   * @param callback should ALWAYS be provided, but is optional to match Forge Viewer types.
   */
  getAccessTokenCb: (callback?: (token: AutodeskToken['accessToken'], expires: number) => void) => void;
  /**
   * Gets an Autodesk cloud token via async/await.
   * Used by `@aps_sdk`.
   * @param scopes unused, but required by `@aps_sdk`
   */
  getAccessTokenAsync: (scopes?: string[]) => Promise<AutodeskToken['accessToken']>;
  /** If true, authentication is in progress, and nothing should be rerendered. */
  loading: boolean;
  /** Redirects the user to the Autodesk Login page */
  redirectToAutodeskLogin: (opts?: { forceLogin?: boolean; state?: AutodeskRedirectState }) => Promise<void>;
  /** `DataManagementClient` with auth provided */
  dataClient: DataManagementClient;
  /** Helper backed by cache */
  getAutodeskProject: (
    autodeskHubId: NonNullable<HubData['id']>,
    autodeskProjectId: ProjectData['id'],
  ) => Promise<ProjectData | null>;
};

const AutodeskAuthContext = React.createContext<AutodeskAuthContextType | undefined>(undefined);

export const AutodeskAuthProvider = ({ children }: { children: ReactNode }) => {
  const refreshTokenPromise = useRef<Promise<AutodeskToken> | null>(null);
  const [autodeskAuth, setAutodeskAuth] = useLocalStorage('AUTODESK_AUTH', undefined);
  const [loading, setLoading] = useState(false);
  const { apiCall: getAutodeskClientId } = useEvolveGet('shop/autodesk/auth');
  const { apiCall: refreshAutodeskTokenApiCall } = useEvolvePost('shop/autodesk/auth/refresh');

  const refreshAutodeskToken = useCallback(
    async (refreshToken: AutodeskToken['refreshToken']): Promise<AutodeskToken> => {
      if (refreshTokenPromise.current) return refreshTokenPromise.current;
      refreshTokenPromise.current = refreshAutodeskTokenApiCall({ refreshToken })
        .then((newToken) => {
          setAutodeskAuth(newToken);
          return newToken;
        })
        .catch((err) => {
          setAutodeskAuth(undefined);
          throw err;
        })
        .finally(() => (refreshTokenPromise.current = null));
      return refreshTokenPromise.current;
    },
    [refreshAutodeskTokenApiCall, setAutodeskAuth],
  );

  const redirectToAutodeskLogin = useCallback<AutodeskAuthContextType['redirectToAutodeskLogin']>(
    async ({ forceLogin = false, state: stateOverride } = {}) => {
      try {
        setLoading(true);
        const { clientId } = await getAutodeskClientId().catch((err) => {
          notifications.show({
            title: 'Something went wrong.',
            message: extractErrorMessage(err),
            color: 'red',
          });
          setLoading(false);
          throw err;
        });
        const state = buildAutodeskStateVar(stateOverride ?? { previousLoc: window.location.pathname });
        const params = {
          client_id: clientId,
          redirect_uri: autodeskRedirectUri,
          response_type: 'code',
          scope: 'data:read',
          state,
          // If set to `login`, will always force the user to re-login
          prompt: forceLogin ? 'login' : undefined,
        };
        const searchParams = new URLSearchParams();
        strongEntries(params).forEach(([key, value]) => {
          if (isNotNil(value)) searchParams.set(key, value);
        });
        window.location.href = `https://developer.api.autodesk.com/authentication/v2/authorize?${searchParams.toString()}`;
      } finally {
        setLoading(false);
      }
    },
    [getAutodeskClientId],
  );

  const fetchAutodeskToken = useCallback(async () => {
    // Pull directly from local storage, in case the Forge Viewer
    // is using an older version of the getAccessToken function
    const existingToken = getLocalStorageWrapped('AUTODESK_AUTH');

    if (isNil(existingToken)) {
      throw new Error('No Autodesk auth token found.');
    }
    return !isExpired(existingToken) ? existingToken : await refreshAutodeskToken(existingToken.refreshToken);
  }, [refreshAutodeskToken]);

  const getAccessTokenAsync = useCallback<AutodeskAuthContextType['getAccessTokenAsync']>(
    async () => fetchAutodeskToken().then((token) => token.accessToken),
    [fetchAutodeskToken],
  );

  const getAccessTokenCb = useCallback<AutodeskAuthContextType['getAccessTokenCb']>(
    (callback) => {
      void fetchAutodeskToken().then((token) =>
        callback
          ? callback(token.accessToken, token.expiresAt - Math.floor(Date.now() / 1000))
          : // oxlint-disable-next-line no-console
            console.warn('No callback provided to getAccessTokenCb'),
      );
    },
    [fetchAutodeskToken],
  );

  const dataClient = useMemo(
    () =>
      new DataManagementClient({
        authenticationProvider: {
          getAccessToken: getAccessTokenAsync,
        },
        sdkManager: SdkManagerBuilder.create()
          .addApsConfiguration(
            new ApsConfiguration({
              baseOptions: {
                headers: {
                  // iOS CORS fix: the SDK explicitly sets User-Agent on every
                  // request, likely assuming it would be silently dropped in
                  // browsers (as Chrome does). However, User-Agent is no longer
                  // a forbidden header per spec, so iOS Safari sends it. Autodesk's
                  // API does not include User-Agent in Access-Control-Allow-Headers,
                  // causing the CORS preflight to fail on iOS. Setting it to
                  // undefined prevents axios from attempting to set it.
                  'User-Agent': undefined,
                },
              },
            }),
          )
          .build(),
        // Resiliency configuration removed while investigating iOS model viewer issues (FWP#1506, PR#1936).
      }),
    [getAccessTokenAsync],
  );

  const projectCacheRef = useRef<Record<AutodeskCloudModel['autodeskProjectId'], Promise<ProjectData | null>>>({});
  const getAutodeskProject = useCallback<AutodeskAuthContextType['getAutodeskProject']>(
    async (autodeskHubId, autodeskProjectId) => {
      if (!isKeyOf(autodeskProjectId, projectCacheRef.current)) {
        projectCacheRef.current = {
          ...projectCacheRef.current,
          [autodeskProjectId]: dataClient
            .getProject(autodeskHubId, autodeskProjectId)
            .then((res) => res.data ?? null)
            // Catch any error and set the project to `null`
            // which will show a "No access" error to the user
            .catch((e) => {
              // oxlint-disable-next-line no-console
              console.error(e);
              return null;
            }),
        };
      }
      return (await projectCacheRef.current[autodeskProjectId]) ?? null;
    },
    [dataClient],
  );

  const value = useMemo<AutodeskAuthContextType>(
    () => ({
      redirectToAutodeskLogin,
      autodeskAuth,
      setAutodeskAuth,
      getAccessTokenAsync,
      getAccessTokenCb,
      loading,

      // Autodesk API clients
      dataClient,

      // Helpers backed by cache
      getAutodeskProject,
    }),
    [
      autodeskAuth,
      dataClient,
      getAccessTokenAsync,
      getAccessTokenCb,
      getAutodeskProject,
      loading,
      redirectToAutodeskLogin,
      setAutodeskAuth,
    ],
  );

  return <AutodeskAuthContext value={value}>{children}</AutodeskAuthContext>;
};

export const useAutodeskAuth = () => useGeneralContext(AutodeskAuthContext, 'AutodeskAuth');
